/*
 * -----------------------------------------------------------------------
 *  <copyright file="line/display.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { formatKey } from '../panel-display';
import {
    HC_COLORS_STANDARD,
    IOpraChartHorizontalLine,
    IOpraChartKey,
    IOpraChartSeries,
    OpraChartLineStyle,
    OpraChartSeriesType,
    OpraColours,
    OpraMarkerShape,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import ScatterLineChart from 'views/components/core/charts/scatter-line-chart';

const getLine = ({ getRef, panelState, sites, siteValues, subjects, siteId, setLoadingComplete, previewMode }: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    if (
        (panelState.chartConfig.seriesType === PanelConstants.seriesType.SITE ||
            panelState.chartConfig.seriesType === PanelConstants.seriesType.SITE_AVERAGE ||
            panelState.chartConfig.seriesType === PanelConstants.seriesType.SITEAVERAGE ||
            panelState.chartConfig.seriesType === PanelConstants.seriesType.SITE_CUMULATIVE ||
            panelState.chartConfig.seriesType === PanelConstants.seriesType.SITECUMULATIVE) &&
        !siteValues.length
    ) {
        if (setLoadingComplete) setLoadingComplete();

        return <h3 className="error-message">Line charts for SITE series require at least one site</h3>;
    }

    const getSiteData = (siteData: any) => {
        const array = [] as IOpraChartSeries[];
        for (let i = 0; i < siteData.length; i++) {
            array.push({
                name: panelState.chartConfig.series[i]?.label ?? panelState.chartConfig[`series${i + 1}label`],
                type: OpraChartSeriesType.Line,
                hasConfidences: false,
                style: {
                    color: HC_COLORS_STANDARD[[4, 5, 6, 7][i]],
                    marker: {
                        shape: [
                            OpraMarkerShape.Circle,
                            OpraMarkerShape.Square,
                            OpraMarkerShape.Diamond,
                            OpraMarkerShape.Triangle,
                        ][i],
                        isHollow: false,
                    },
                },
                data: siteData[i]
                    ? siteData[i].map((plot: any) => {
                          const date = new Date(plot.x);
                          return {
                              x: Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
                              y: plot.y,
                              site: siteId ? sites[siteId]?.Fields['Site.name'] : undefined,
                          };
                      })
                    : [],
            });
        }
        return array;
    };

    const getSiteCumulativeData = (siteData: any) => {
        const array = [];
        for (let i = 0; i < siteData.length; i++) {
            let cumulativeTotal = 0;
            array.push({
                name: panelState.chartConfig.series[i]?.label ?? panelState.chartConfig[`series${i + 1}label`],
                type: OpraChartSeriesType.Line,
                hasConfidences: false,
                style: {
                    color: HC_COLORS_STANDARD[[4, 5, 6, 7][i]],
                    marker: {
                        shape: [
                            OpraMarkerShape.Circle,
                            OpraMarkerShape.Square,
                            OpraMarkerShape.Diamond,
                            OpraMarkerShape.Triangle,
                        ][i],
                        isHollow: false,
                    },
                },
                data: siteData[i]
                    ? siteData[i].map((plot: any) => {
                          const date = new Date(plot.x);
                          cumulativeTotal += plot.y;
                          return {
                              x: Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
                              y: cumulativeTotal,
                              site: siteId ? sites[siteId]?.Fields['Site.name'] : undefined,
                          };
                      })
                    : [],
            });
        }
        return array;
    };

    const getLineSeries = () => {
        switch (panelState.chartConfig.seriesType) {
            case PanelConstants.seriesType.SUBJECT:
                const sbjData =
                    siteId && panelState.panelData.panelData[0].lineSubjectForSites
                        ? panelState.panelData.panelData[0].lineSubjectForSites[siteId]
                        : panelState.panelData.panelData[0].lineSubjectForSubjects;

                if (!sbjData) {
                    console.log('No Subject Data for line series: ', panelState.panelData);
                    return [];
                }
                return Object.keys(sbjData).map(
                    (subjectId: string): IOpraChartSeries => ({
                        name: subjects[subjectId]?.Fields['Subject.subjectNumber'] ?? 'Unknown subject code',
                        type: OpraChartSeriesType.Line,
                        hasConfidences: false,
                        data: sbjData[subjectId].map((plot: any) => {
                            const date = new Date(plot.x);
                            return {
                                x: Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
                                y: plot.y,
                            };
                        }),
                    }),
                );

            case PanelConstants.seriesType.SITE:
                const siteData =
                    siteId && panelState.panelData.panelData[0].lineSiteForSite
                        ? panelState.panelData.panelData[0].lineSiteForSite.map((s: any) => s[siteId])
                        : panelState.panelData.panelData[0].lineSiteForStudy;
                return getSiteData(siteData);

            case PanelConstants.seriesType.SITE_AVERAGE:
            case PanelConstants.seriesType.SITEAVERAGE:
                const siteAverageData =
                    siteId && panelState.panelData.panelData[0].lineAverageForSite
                        ? {
                              name: sites[siteId].Fields['Site.name'],
                              color: HC_COLORS_STANDARD[OpraColours.DarkBlue],
                              data: panelState.panelData.panelData[0].lineAverageForSite[siteId] || [],
                          }
                        : {
                              name: 'All Sites',
                              color: HC_COLORS_STANDARD[OpraColours.LightBlue],
                              data: panelState.panelData.panelData[0].lineAverageForStudy,
                          };

                return [
                    {
                        name: siteAverageData.name,
                        type: OpraChartSeriesType.Line,
                        hasConfidences: false,
                        style: {
                            color: siteAverageData.color,
                            marker: {
                                shape: OpraMarkerShape.Circle,
                                isHollow: false,
                            },
                        },
                        data: siteAverageData.data.map((plot: any) => {
                            const date = new Date(plot.x);
                            return {
                                x: Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
                                y: plot.y,
                            };
                        }),
                    },
                ] as IOpraChartSeries[];

            case PanelConstants.seriesType.EVENT:
                const eventData =
                    siteId && panelState?.panelData?.panelData[0].lineEventForSite
                        ? panelState.panelData.panelData[0].lineEventForSite[siteId]
                        : panelState.panelData.panelData[0].lineEventForStudy;
                return Object.keys(eventData || []).map(
                    (cat: string): IOpraChartSeries => ({
                        name: cat,
                        type: OpraChartSeriesType.Line,
                        hasConfidences: false,
                        data: eventData[cat].map((plot: any) => {
                            const date = new Date(plot.x);
                            return {
                                x: Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
                                y: plot.y,
                            };
                        }),
                    }),
                );

            case PanelConstants.seriesType.SITE_CUMULATIVE:
            case PanelConstants.seriesType.SITECUMULATIVE:
                const siteCumulativeData =
                    siteId && panelState.panelData.panelData[0].lineSiteForSite
                        ? panelState.panelData.panelData[0].lineSiteForSite.map((s: any) => s[siteId])
                        : panelState.panelData.panelData[0].lineSiteForStudy;
                return getSiteCumulativeData(siteCumulativeData);

            default:
                console.warn(`Unknown panel type: ${panelState.chartConfig.seriesType}`);
                return [];
        }
    };

    const getHorizontalLines = () => {
        switch (panelState.chartConfig.seriesType) {
            case PanelConstants.seriesType.SITE_CUMULATIVE:
            case PanelConstants.seriesType.SITECUMULATIVE:
                const array = [];
                for (let i = 0; i < panelState.panelData.panelData[0].lineSiteThresholds.length; i++) {
                    if (
                        panelState.panelData.panelData[0].lineSiteThresholds[i] !== null &&
                        panelState.panelData.panelData[0].lineSiteThresholds[i] !== ''
                    ) {
                        array.push({
                            name: '',
                            color: HC_COLORS_STANDARD[[4, 5, 6, 7][i]],
                            style: OpraChartLineStyle.Dashed,
                            y: panelState.panelData.panelData[0].lineSiteThresholds[i],
                        });
                    }
                }
                return array;
            case PanelConstants.seriesType.SITE_AVERAGE:
            case PanelConstants.seriesType.SITEAVERAGE:
                if (
                    panelState.panelData.panelData[0].threshold1 >= 0 &&
                    panelState.panelData.panelData[0].threshold2 >= 0
                ) {
                    return [
                        {
                            name: '',
                            color: HC_COLORS_STANDARD[OpraColours.Amber],
                            style: OpraChartLineStyle.Dashed,
                            y: panelState.panelData.panelData[0].threshold1,
                        },
                        {
                            name: '',
                            color: HC_COLORS_STANDARD[OpraColours.Red],
                            style: OpraChartLineStyle.Dashed,
                            y: panelState.panelData.panelData[0].threshold2,
                        },
                    ] as IOpraChartHorizontalLine[];
                }
                return [];
            default:
                return [];
        }
    };

    const getKey = () => {
        switch (panelState.chartConfig.seriesType) {
            case PanelConstants.seriesType.SITE_CUMULATIVE:
            case PanelConstants.seriesType.SITECUMULATIVE:
                const array = [];
                for (let i = 0; i < panelState.panelData.panelData[0].lineSiteThresholds.length; i++) {
                    console.warn(`Threshold ${i}: '${panelState.panelData.panelData[0].lineSiteThresholds[i]}'`);

                    if (
                        panelState.panelData.panelData[0].lineSiteThresholds[i] !== null &&
                        panelState.panelData.panelData[0].lineSiteThresholds[i] !== ''
                    ) {
                        array.push({
                            label: '',
                            value: panelState.panelData.panelData[0].lineSiteThresholds[i],
                            // Convert from HC_COLORS_STANDARD[[4, 5, 6, 7][i]],
                            colour: 4 + i,
                        });
                    }
                }

                return {
                    text: formatKey([array], false),
                    position: OpraVerticalAlign.bottom,
                };
            case PanelConstants.seriesType.SITE_AVERAGE:
            case PanelConstants.seriesType.SITEAVERAGE:
                const studyMedianMean: boolean =
                    panelState.chartConfig.lineThresholdType === PanelConstants.panelThresholdType.STUDY_MEDIAN_MEAN ||
                    panelState.chartConfig.lineThresholdType === PanelConstants.panelThresholdType.STUDYMEDIANMEAN;

                return {
                    text: formatKey(
                        [
                            [
                                {
                                    label: studyMedianMean
                                        ? PanelConstants.lineThresholdType.STUDYMEDIAN
                                        : PanelConstants.lineThresholdType.STUDYSPECIFIC,
                                    value: panelState.panelData.panelData[0].threshold1,
                                    colour: OpraColours.Amber,
                                },
                                {
                                    label: studyMedianMean
                                        ? PanelConstants.lineThresholdType.STUDYMEAN
                                        : PanelConstants.lineThresholdType.STUDYSPECIFIC,
                                    value: panelState.panelData.panelData[0].threshold2,
                                    colour: OpraColours.Red,
                                },
                            ],
                        ],
                        false,
                    ),
                    position: OpraVerticalAlign.bottom,
                } as IOpraChartKey;
            default:
                return undefined;
        }
    };

    const lineSeries = getLineSeries();

    let checkingIfLineSeriesEmpty = true;
    lineSeries.forEach((item: any) => {
        if (item?.data?.length) {
            checkingIfLineSeriesEmpty = false;
            return false;
        }
    });

    const horizontalLines = getHorizontalLines();
    const key = getKey();
    const tooltipShowSeriesLabel =
        panelState.chartConfig.seriesType !== PanelConstants.seriesType.SITE_AVERAGE &&
        panelState.chartConfig.seriesType !== PanelConstants.seriesType.SITEAVERAGE;
    const tooltipShowSeriesThreshold = true;

    const getMaxY = () => {
        if (!horizontalLines?.length) return undefined;
        if (!lineSeries?.length) return undefined;
        const maxThreshold = Math.max(...horizontalLines.map((x) => x.y));
        const yPlots = (lineSeries as IOpraChartSeries[]).flatMap((x) => x?.data?.map((xx) => xx.y));
        const maxPlot = Math.max(...yPlots);
        if (maxThreshold > maxPlot) return maxThreshold;
        return undefined;
    };

    const maxY = getMaxY();

    const getMinY = () => {
        if (!horizontalLines?.length) return undefined;
        if (!lineSeries?.length) return undefined;
        const minThreshold = Math.min(...horizontalLines.map((line) => line.y));
        const yPlots = (lineSeries as IOpraChartSeries[])
            .flatMap((series) => series?.data?.map((plot) => plot.y))
            .filter((y) => y !== null);
        const minPlot = Math.min(...yPlots);
        if (minThreshold < minPlot) return minThreshold;
        return undefined;
    };

    if (!checkingIfLineSeriesEmpty || !previewMode)
        return (
            <ScatterLineChart
                getRef={getRef}
                plotTitle={panelState.chartConfig.plotTitle}
                showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
                xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: true }}
                yAxisTitle={panelState.chartConfig.yTitle}
                series={lineSeries}
                turboModeThreshold={5000}
                isCustomLegendWidth={false}
                horizontalLines={horizontalLines}
                secondaryKey={key}
                tooltipShowSeriesLabel={tooltipShowSeriesLabel}
                tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
                maxY={maxY}
                minY={getMinY()}
                hidePlotTitle={panelState.hidePlotTitle}
            />
        );

    return <h1 className="no-data">The preview cannot be displayed as data is not available</h1>;
};

export { getLine };
